/**
 * Updates the product SKU after a variant swatch, button or dropdown changes.
 *
 * This implementation does not depend on Horizon's version-specific
 * ProductSelectEvent event name. It reads the selected variant JSON that
 * Horizon already renders inside <variant-picker>.
 */
(() => {
  const SKU_BLOCK_SELECTOR = '.allvinyl_product__sku';
  const MAX_ATTEMPTS = 80;
  const CHECK_INTERVAL = 50;

  /**
   * Find the product section belonging to a variant picker.
   *
   * @param {Element} element
   * @returns {Element|Document}
   */
  function getProductContext(element) {
    return (
      element.closest(
        '[id*="ProductInformation-"], featured-product-information, dialog, .shopify-section'
      ) || document
    );
  }

  /**
   * Read the raw selected-variant JSON from a variant picker.
   *
   * @param {Element|null} picker
   * @returns {string}
   */
  function getVariantJson(picker) {
    if (!picker) return '';

    return (
      picker
        .querySelector('script[type="application/json"]')
        ?.textContent?.trim() || ''
    );
  }

  /**
   * Parse the selected variant.
   *
   * A null value represents an unavailable variant combination.
   *
   * @param {string} json
   * @returns {object|null|undefined}
   */
  function parseVariant(json) {
    if (!json) return undefined;

    try {
      return JSON.parse(json);
    } catch (error) {
      console.warn(
        '[AllVinyl SKU] Could not parse variant data:',
        error
      );

      return undefined;
    }
  }

  /**
   * Re-find the picker if Horizon replaced it during the update.
   *
   * @param {Element|Document} context
   * @param {string} productId
   * @param {Element} originalPicker
   * @returns {Element|null}
   */
  function findActivePicker(context, productId, originalPicker) {
    if (originalPicker.isConnected) {
      return originalPicker;
    }

    const pickers = Array.from(
      context.querySelectorAll('variant-picker')
    );

    return (
      pickers.find(
        (picker) =>
          !productId ||
          picker.getAttribute('data-product-id') === productId
      ) ||
      pickers[0] ||
      null
    );
  }

  /**
   * Find the SKU block belonging to the selected product.
   *
   * @param {Element|Document} context
   * @param {string} productId
   * @returns {HTMLElement|null}
   */
  function findSkuBlock(context, productId) {
    const localBlocks = Array.from(
      context.querySelectorAll(SKU_BLOCK_SELECTOR)
    );

    const localBlock =
      localBlocks.find(
        (block) =>
          !productId ||
          block.getAttribute('data-product-id') === productId
      ) ||
      localBlocks[0] ||
      null;

    if (localBlock instanceof HTMLElement) {
      return localBlock;
    }

    const globalBlocks = Array.from(
      document.querySelectorAll(SKU_BLOCK_SELECTOR)
    );

    const globalBlock =
      globalBlocks.find(
        (block) =>
          !productId ||
          block.getAttribute('data-product-id') === productId
      ) || null;

    return globalBlock instanceof HTMLElement
      ? globalBlock
      : null;
  }

  /**
   * Update the visible SKU.
   *
   * @param {HTMLElement} skuBlock
   * @param {object|null} variant
   */
  function updateSku(skuBlock, variant) {
    const valueElement = skuBlock.querySelector(
      '.allvinyl_product__sku-value'
    );

    if (!valueElement) return;

    const sku =
      typeof variant?.sku === 'string'
        ? variant.sku.trim()
        : '';

    valueElement.textContent = sku;
    skuBlock.hidden = sku === '';
  }

  /*
   * Capture the native change event before Horizon starts replacing
   * parts of the variant picker.
   */
  document.addEventListener(
    'change',
    (event) => {
      if (!(event.target instanceof Element)) return;

      const picker = event.target.closest('variant-picker');

      /*
       * Ignore collection-card swatches. This code is intended for the
       * main product page and featured-product sections.
       */
      if (!picker || picker.closest('product-card')) return;

      const originalContext = getProductContext(picker);
      const productId =
        picker.getAttribute('data-product-id') || '';

      /*
       * Store the current JSON. After Horizon resolves the swatch
       * selection, this JSON changes to the newly selected variant.
       */
      const previousJson = getVariantJson(picker);
      let attempts = 0;

      function checkForUpdatedVariant() {
        const activePicker = findActivePicker(
          originalContext,
          productId,
          picker
        );

        const currentJson = getVariantJson(activePicker);

        if (currentJson && currentJson !== previousJson) {
          const variant = parseVariant(currentJson);
          const currentContext = activePicker
            ? getProductContext(activePicker)
            : originalContext;

          const skuBlock = findSkuBlock(
            currentContext,
            productId
          );

          if (skuBlock) {
            updateSku(skuBlock, variant);
          }

          return;
        }

        attempts += 1;

        if (attempts < MAX_ATTEMPTS) {
          window.setTimeout(
            checkForUpdatedVariant,
            CHECK_INTERVAL
          );
        }
      }

      window.setTimeout(checkForUpdatedVariant, 0);
    },
    true
  );
})();